import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";

import { Constants } from "../models/Constants";
import { StatusCode } from "../models/Enums";
import { ErrorModel } from "../models/ErrorModel";
import { loaderService } from "./notificationService";
import { tokenService } from "./tokenService";

export const instance: AxiosInstance = axios.create({
  baseURL: Constants.Api.baseUrl,
  timeout: 300000, // 5 minutes timeout - extended for global users with high latency
  withCredentials: true, // Required for cookie-based authentication
  headers: {
    "Content-Type": "application/json",
  },
  maxRedirects: 0, // Prevent Axios from following redirects (especially auth redirects that cause CORS issues)
  // Axios automatically handles connection pooling via browser's HTTP/1.1 keep-alive
});

// Retry configuration for global deployment
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second base delay

// Helper function to determine if error is retryable
const isRetryableError = (error: AxiosError): boolean => {
  if (!error.response) {
    // Network errors, timeouts, etc. - retry these
    return true;
  }

  const status = error.response.status;

  // Always retry these specific errors (transient issues)
  if (
    status === 408 ||
    status === 429 ||
    status === 502 ||
    status === 503 ||
    status === 504
  ) {
    return true;
  }

  // For 500 errors, check if it's a transient error or a real application error
  if (status === 500) {
    const errorMessage =
      error.response.data?.Message || error.response.data?.message || "";

    // Don't retry database errors, validation errors, or application errors
    const nonRetryablePatterns = [
      "database error",
      "validation",
      "invalid",
      "not found",
      "unauthorized",
      "forbidden",
      "bad request",
    ];

    const isNonRetryable = nonRetryablePatterns.some(pattern =>
      errorMessage.toLowerCase().includes(pattern)
    );

    // Only retry if it's NOT a known application error
    return !isNonRetryable;
  }

  return false;
};

// Helper function to calculate exponential backoff delay
const getRetryDelay = (retryCount: number): number => {
  return RETRY_DELAY * Math.pow(2, retryCount); // Exponential backoff: 1s, 2s, 4s
};

// Token refresh state management to prevent race conditions
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

/**
 * Refresh the access token using the refresh token
 * Prevents multiple simultaneous refresh requests using a shared promise
 */
const refreshAccessToken = async (): Promise<any> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    console.log(
      "[TOKEN REFRESH] Already refreshing, waiting for existing refresh..."
    );
    return refreshPromise;
  }

  isRefreshing = true;
  console.log("[TOKEN REFRESH] Starting token refresh...");

  refreshPromise = (async () => {
    try {
      const refreshToken = tokenService.getRefreshToken();
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const newTokens = await tokenService.refreshToken(refreshToken);
      tokenService.storeTokens(newTokens);
      console.log("[TOKEN REFRESH] Token refresh successful");
      return newTokens;
    } catch (error) {
      console.error("[TOKEN REFRESH] Token refresh failed:", error);
      // Clear tokens on refresh failure
      tokenService.clearTokens();
      localStorage.removeItem("userInfo");
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

instance.interceptors.request.use(
  async (request: AxiosRequestConfig) => {
    // Only show loader for the initial request, not for retries.
    // The loader uses an array counter - each setLoading(true) pushes an entry,
    // each clearLoading removes one. Retries would push extra entries that never get cleared.
    // Skip the global loader entirely for file uploads (they use inline status instead).
    if (!request.metadata || request.metadata.retryCount === 0) {
      if (!(request as any).skipGlobalLoader) {
        loaderService.setLoading(true);
      }
    }

    // Initialize retry count and metadata
    if (!request.metadata) {
      request.metadata = {
        startTime: new Date(),
        retryCount: 0,
      };
    }

    // Add JWT Bearer token to requests (except auth endpoints)
    // Note: baseURL already includes '/api', so auth endpoints are '/auth/*'
    const isAuthEndpoint = request.url?.includes("/auth/");
    if (!isAuthEndpoint) {
      const accessToken = tokenService.getAccessToken();
      const refreshToken = tokenService.getRefreshToken();

      // CRITICAL: Skip token logic entirely for unauthenticated users
      // If there's no access token AND no refresh token, user is not logged in
      // Let the request proceed without Authorization header (will get 401 if auth is required)
      if (!accessToken && !refreshToken) {
        // User is not authenticated - skip token logic
        // The backend will return 401 if authentication is required
        // This prevents errors on initial page load for unauthenticated users
        return request;
      }

      // User has tokens (authenticated or previously authenticated)
      // Check if token is expired or about to expire and try to refresh
      if (tokenService.isTokenExpired()) {
        console.log(
          "[TOKEN REFRESH] Token expired or about to expire, refreshing..."
        );

        if (refreshToken) {
          try {
            // Use the shared refresh function to prevent race conditions
            const newTokens = await refreshAccessToken();

            // Use new access token for this request
            if (request.headers) {
              request.headers.Authorization = `Bearer ${newTokens.accessToken}`;
            }
          } catch (error) {
            // Refresh failed - clear tokens and redirect to login
            console.error(
              "[TOKEN REFRESH] Refresh failed, redirecting to login"
            );
            loaderService.clearLoading();

            // Redirect to home page (MSAL will handle login)
            const currentPath = window.location.pathname;
            if (!currentPath.includes("/Account/") && currentPath !== "/") {
              window.location.href = `/`;
            }

            // Reject the request with proper error structure
            const rejectionError: any = new ErrorModel(
              "Session expired. Please log in again.",
              StatusCode.UnAuthorized
            );
            // Preserve the original request config for error handler
            rejectionError.config = request;
            return Promise.reject(rejectionError);
          }
        } else {
          // Has access token but no refresh token - unusual state, clear and redirect
          console.error(
            "[TOKEN REFRESH] No refresh token available, clearing tokens"
          );
          tokenService.clearTokens();
          localStorage.removeItem("userInfo");
          loaderService.clearLoading();

          const currentPath = window.location.pathname;
          if (!currentPath.includes("/Account/") && currentPath !== "/") {
            window.location.href = `/`;
          }

          const rejectionError: any = new ErrorModel(
            "Session expired. Please log in again.",
            StatusCode.UnAuthorized
          );
          rejectionError.config = request;
          return Promise.reject(rejectionError);
        }
      } else if (accessToken) {
        // Token is still valid, use it
        if (request.headers) {
          request.headers.Authorization = `Bearer ${accessToken}`;
        }
      }
    }

    // Note: Connection keep-alive is automatically handled by the browser and axios
    // No need to manually set the Connection header (browsers block it anyway)
    return request;
  },
  (error: any) => {
    loaderService.clearLoading();

    // Handle cases where error.request is undefined
    if (!error.request) {
      throw new ErrorModel(
        error.message || "Request error occurred",
        StatusCode.InternalServerError
      );
    }

    const { data, status } = error.request;
    throw new ErrorModel(data?.Message, status);
  }
);

// Add a response interceptor
instance.interceptors.response.use(
  function (response: any) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    if (!response.config?.skipGlobalLoader) {
      loaderService.clearLoading();
    }

    // DEBUG: Log response details for auth/exchange
    if (response.config?.url?.includes("/auth/exchange")) {
      console.log("[AXIOS SUCCESS] Response received:", {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers["content-type"],
        headers: response.headers,
        dataType: typeof response.data,
        data: response.data,
      });
    }

    // Log request duration in development
    if (response.config?.metadata?.startTime) {
      const duration =
        new Date().getTime() - response.config.metadata.startTime.getTime();
      if (duration > 5000 && process.env.NODE_ENV === "development") {
        console.warn(
          `Slow request detected: ${response.config.url} took ${duration}ms`
        );
      }
    }

    if (response.headers["content-type"] === "text/html; charset=utf-8") {
      throw new ErrorModel(
        `UnAuthorized access. You will be redirected to login page in #TIMER# seconds.`,
        StatusCode.UnAuthorized
      );
    }
    return response;
  },
  async function (error: any) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    const config = error.config;

    // If config is undefined, this is likely a request that was rejected before being sent
    // (e.g., token refresh failure in request interceptor)
    if (!config) {
      loaderService.clearLoading();
      return Promise.reject(error);
    }

    const skipLoader = config.skipGlobalLoader;

    // Initialize retry count if not present
    if (!config.metadata) {
      config.metadata = { retryCount: 0 };
    }

    // DEBUG: Detailed logging for auth/exchange errors
    if (config?.url?.includes("/auth/exchange")) {
      console.error("[AXIOS ERROR] Full error details:", {
        url: config?.url,
        method: config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        code: error.code,
        message: error.message,
        responseHeaders: error.response?.headers,
        responseData: error.response?.data,
        retryCount: config.metadata?.retryCount || 0,
      });
    }

    // Log error details in development
    if (process.env.NODE_ENV === "development") {
      console.error("API Error:", {
        url: config?.url,
        method: config?.method,
        status: error.response?.status,
        code: error.code,
        message: error.message,
        retryCount: config.metadata?.retryCount || 0,
      });
    }

    // Handle 401 Unauthorized - session expired, redirect to re-authenticate (DO NOT RETRY)
    // IMPORTANT: Skip 401 handling for auth endpoints (/auth/exchange, /auth/refresh)
    // because these endpoints return 401 for expected scenarios:
    // - /auth/exchange: User not found in database (should show error message, not redirect)
    // - /auth/refresh: Refresh token expired (should be handled by request interceptor)
    const isAuthEndpoint = config?.url?.includes("/auth/");
    if (error.response && error.response.status === 401 && !isAuthEndpoint) {
      if (!skipLoader) loaderService.clearLoading();

      console.log(
        "[AXIOS 401] Non-auth endpoint returned 401, clearing tokens and redirecting to login"
      );

      // Clear JWT tokens and user info
      tokenService.clearTokens();
      localStorage.removeItem("userInfo");

      const currentPath = window.location.pathname;
      // Only redirect if not already on the login page
      if (!currentPath.includes("/Account/") && currentPath !== "/") {
        // Redirect to home page (MSAL will handle login)
        window.location.href = `/`;
      }
      return Promise.reject(
        new ErrorModel(
          "Session expired. Please log in again.",
          StatusCode.UnAuthorized
        )
      );
    }

    // Don't retry file upload timeouts -- re-uploading large files is wasteful
    const isFileUpload = config.headers?.["Content-Type"] === "multipart/form-data"
      || config.data instanceof FormData;
    const isTimeout = error.code === "ECONNABORTED" || error.message?.includes("timeout");
    const shouldSkipRetry = isFileUpload && isTimeout;

    // Retry logic for transient errors (network issues, timeouts, server errors)
    if (!shouldSkipRetry && isRetryableError(error) && config.metadata.retryCount < MAX_RETRIES) {
      config.metadata.retryCount += 1;
      const retryDelay = getRetryDelay(config.metadata.retryCount - 1);

      if (process.env.NODE_ENV === "development") {
        console.warn(
          `Retrying request (${config.metadata.retryCount}/${MAX_RETRIES}) after ${retryDelay}ms: ${config.url}`
        );
      }

      // Don't clear loading - keep spinner during retry
      await new Promise(resolve => setTimeout(resolve, retryDelay));

      // Retry the request
      return instance.request(config);
    }

    // All retries exhausted or non-retryable error
    if (!skipLoader) loaderService.clearLoading();

    // Handle cases where error.response is undefined (network errors, CORS, timeout, etc.)
    if (!error.response) {
      // Check if it's a timeout error
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        return Promise.reject(
          new ErrorModel(
            "Request timeout after multiple attempts. The server may be experiencing high load. Please try again later.",
            StatusCode.InternalServerError
          )
        );
      }

      // Check if it's a network error (server unreachable)
      if (error.message === "Network Error" || error.code === "ERR_NETWORK") {
        return Promise.reject(
          new ErrorModel(
            "Unable to connect to the server after multiple attempts. Please check your internet connection or try again later.",
            StatusCode.InternalServerError
          )
        );
      }

      // Generic network error
      return Promise.reject(
        new ErrorModel(
          error.message ||
            "Network error occurred after retrying. Please check your connection and try again.",
          StatusCode.InternalServerError
        )
      );
    }

    // Handle server errors with response
    const { data, status } = error.response;
    return Promise.reject(
      new ErrorModel(
        data?.Message || data?.message || "An error occurred",
        status
      )
    );
  }
);
